// CompetencyModel — unified module
// Overview reads:  competency_category (tabs) + corp_competence_groups/items & func_competence_groups/items
// Configure writes: same old tables (data-compatible with evaluations module)
// Descriptions:    competency_descriptions

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../context/AuthContext';
import './competencyModel.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

// Categories whose name contains 'core' or 'corporate' map to corp_* tables.
const isCoreCategory = name => {
  const l = (name || '').toLowerCase();
  return l.includes('core') || l.includes('corporate');
};

const LEVEL_NAMES = ['Entry Level', 'Operational Excellence', 'Functional Accountability', 'Growth and Sustainability'];
const getLevelName = i => LEVEL_NAMES[i - 1] || `Level ${i}`;
const CONFIG_STEPS = ['Categories', 'Groups', 'Competencies', 'Descriptions'];

// ── Shared small components ───────────────────────────────────────────────────

const Breadcrumb = ({ step, onNav }) => (
  <nav className="cm__breadcrumb">
    {CONFIG_STEPS.map((label, i) => (
      <React.Fragment key={i}>
        <button
          type="button"
          className={`cm__bc-item ${i === step - 1 ? 'cm__bc-item--active' : ''} ${i < step - 1 ? 'cm__bc-item--done' : ''}`}
          onClick={() => i < step - 1 && onNav(i + 1)}
          disabled={i >= step - 1}
        >
          <span className="cm__bc-num">{i + 1}</span>
          <span className="cm__bc-label">{label}</span>
        </button>
        {i < CONFIG_STEPS.length - 1 && <i className="bi bi-chevron-right cm__bc-sep" />}
      </React.Fragment>
    ))}
  </nav>
);

const ConfirmModal = ({ message, onCancel, onConfirm }) => (
  <div className="cm__modal-overlay">
    <div className="cm__modal">
      <h3 className="cm__modal-title">Confirm Delete</h3>
      <p className="cm__modal-body">{message}</p>
      <div className="cm__modal-actions">
        <button className="cm__modal-cancel" onClick={onCancel}>Cancel</button>
        <button className="cm__modal-confirm" onClick={onConfirm}>Delete</button>
      </div>
    </div>
  </div>
);

const UndoToast = ({ message, countdown, onUndo }) => (
  <div className="cm__undo-toast">
    <span>{message}</span>
    <button className="cm__undo-btn" type="button" onClick={onUndo}>Undo ({countdown}s)</button>
  </div>
);

const Feedback = ({ msg, color }) =>
  msg ? <div className={`cm__feedback cm__feedback--${color}`}>{msg}</div> : null;

// ── Overview: shared group display table ──────────────────────────────────────

const GroupTable = ({ groups, weightKey }) => {
  if (!groups || groups.length === 0)
    return <div className="cm__no-data">No competency groups configured yet. Click Configure to add groups.</div>;
  return (
    <table className="cm__display-table">
      <thead>
        <tr>
          <th className="cm__col-group">Competency Group</th>
          <th className="cm__col-weight">Weighting</th>
          <th className="cm__col-comps">Competencies</th>
        </tr>
      </thead>
      <tbody>
        {groups.map((g, i) => (
          <tr key={g.group_id}>
            <td className="cm__td-group">
              <div>
                <span className="cm__group-alpha">{String.fromCharCode(65 + i)}.</span>
                <span className="cm__group-name">{g.group_name}</span>
              </div>
              {g.description && <p className="cm__group-desc">{g.description}</p>}
            </td>
            <td className="cm__td-weight">{g[weightKey]}%</td>
            <td className="cm__td-comps">
              <ol className="cm__comps-list">
                {(g.items || []).map((c, idx) => (
                  <li key={idx}>{c.competency_name || c.item_name}</li>
                ))}
              </ol>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Overview ──────────────────────────────────────────────────────────────────

const Overview = ({ organizationId, onConfigure }) => {
  const [categories, setCategories]     = useState([]);
  const [activeTab, setActiveTab]       = useState(null); // { category_id, category_name }
  const [coreGroups, setCoreGroups]     = useState([]);
  const [techGroups, setTechGroups]     = useState([]);
  const [departments, setDepartments]   = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState(null);
  const [loading, setLoading]           = useState(false);

  // Load categories once
  useEffect(() => {
    if (!organizationId) return;
    supabase.from('competency_category')
      .select('category_id, category_name, category_weight')
      .eq('organization_id', organizationId)
      .order('created_at')
      .then(({ data }) => {
        const rows = data || [];
        setCategories(rows);
        if (rows.length > 0) setActiveTab(rows[0]);
      });
  }, [organizationId]);

  // Load departments once (for functional tabs)
  useEffect(() => {
    if (!organizationId) return;
    supabase.from('department')
      .select('department_id, depart_name')
      .eq('organization_id', organizationId)
      .order('depart_name')
      .then(({ data }) => {
        const rows = data || [];
        setDepartments(rows);
        if (rows.length > 0) setSelectedDeptId(rows[0].department_id);
      });
  }, [organizationId]);

  // Load groups when active tab or selected dept changes
  useEffect(() => {
    if (!activeTab || !organizationId) return;
    setLoading(true);
    setCoreGroups([]); setTechGroups([]);

    const isCore = isCoreCategory(activeTab.category_name);

    if (isCore) {
      supabase.from('corp_competence_groups')
        .select('group_id, group_name, weighting, description, items:corp_competence_items(competency_id, competency_name)')
        .eq('organization_id', organizationId)
        .order('created_at')
        .then(({ data }) => { setCoreGroups(data || []); setLoading(false); });
    } else {
      if (!selectedDeptId) { setLoading(false); return; }
      supabase.from('func_competence_groups')
        .select('group_id, group_name, group_weight, description, items:func_competence_items(item_id, item_name)')
        .eq('department_id', selectedDeptId)
        .eq('organization_id', organizationId)
        .order('created_at')
        .then(({ data }) => { setTechGroups(data || []); setLoading(false); });
    }
  }, [activeTab, selectedDeptId, organizationId]);

  const isCurrentCore = activeTab ? isCoreCategory(activeTab.category_name) : true;

  return (
    <div className="cm__overview">
      <div className="cm__topbar">
        <div className="cm__topbar-left">
          <h1 className="cm__title"><i className="bi bi-bullseye" /> Competency Model</h1>
          <p className="cm__subtitle">
            View and manage your organisation's competency model — categories, groups, and competencies.
          </p>
        </div>
        <button className="cm__btn cm__btn--configure" onClick={onConfigure}>
          <i className="bi bi-gear" /> Configure
        </button>
      </div>
      <hr className="cm__hr" />

      {/* Category tabs */}
      <div className="cm__tabs">
        {categories.map(cat => (
          <button
            key={cat.category_id}
            className={`cm__tab ${activeTab?.category_id === cat.category_id ? 'cm__tab--active' : ''}`}
            onClick={() => setActiveTab(cat)}
          >
            <i className="bi bi-layers" />
            {cat.category_name}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="cm__loading"><span className="cm__spinner" /> Loading…</div>
      ) : (
        <div className="cm__tab-body">
          {isCurrentCore ? (
            <div className="cm__section">
              <div className="cm__section-header">
                <h3 className="cm__section-heading">
                  <i className="bi bi-building" /> Corporate Talent Strategy
                </h3>
                <p className="cm__section-desc">
                  <strong>What is your organisation's talent strategy?</strong>{' '}
                  Competencies that apply across the entire organisation.
                </p>
              </div>
              <GroupTable groups={coreGroups} weightKey="weighting" />
            </div>
          ) : (
            <div className="cm__section">
              <div className="cm__section-header">
                <h3 className="cm__section-heading">
                  <i className="bi bi-diagram-3" /> Functional Talent Strategy
                </h3>
                <p className="cm__section-desc">
                  <strong>What is your function's talent strategy?</strong>{' '}
                  Competencies specific to individual departments.
                </p>
              </div>
              {departments.length > 0 && (
                <div className="cm__dept-filter">
                  <label className="cm__dept-label" htmlFor="cm-dept-select">Department</label>
                  <select
                    id="cm-dept-select"
                    className="cm__dept-select"
                    value={selectedDeptId ?? ''}
                    onChange={e => setSelectedDeptId(Number(e.target.value))}
                  >
                    {departments.map(d => (
                      <option key={d.department_id} value={d.department_id}>{d.depart_name}</option>
                    ))}
                  </select>
                </div>
              )}
              <GroupTable groups={techGroups} weightKey="group_weight" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Welcome screen ────────────────────────────────────────────────────────────

const WelcomeScreen = ({ onGetStarted }) => (
  <div className="cm__welcome">
    <div className="cm__welcome-card">
      <div className="cm__welcome-icon"><i className="bi bi-layers" /></div>
      <h2 className="cm__welcome-heading">Let's Define Your <strong>Competency Model</strong></h2>
      <p className="cm__welcome-desc">
        Establish the foundational skills, behaviours, and knowledge required for success
        across your organisation. Building your model is the first step toward effective
        talent assessment and development.
      </p>
      <div className="cm__welcome-illustration">
        <i className="bi bi-diagram-3" />
        <i className="bi bi-arrow-right" />
        <i className="bi bi-people" />
        <i className="bi bi-arrow-right" />
        <i className="bi bi-graph-up-arrow" />
      </div>
      <button className="cm__welcome-btn" onClick={onGetStarted}>
        <i className="bi bi-plus-circle" /> Get Started &amp; Create Model
      </button>
      <p className="cm__welcome-hint">Don't worry, you can always refine your model later.</p>
    </div>
  </div>
);

// ── Configure Step 1: Categories ──────────────────────────────────────────────

const Step1Categories = ({ organizationId, onNext }) => {
  const [categories, setCategories]         = useState([]);
  const [catName, setCatName]               = useState('');
  const [catWeight, setCatWeight]           = useState('');
  const [editItem, setEditItem]             = useState(null);
  const [deleteConfirm, setDeleteConfirm]   = useState(null);
  const [feedback, setFeedback]             = useState({ msg: '', color: '' });

  const fetchCategories = useCallback(async () => {
    const { data } = await supabase.from('competency_category')
      .select('*').eq('organization_id', organizationId).order('created_at');
    setCategories(data || []);
  }, [organizationId]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const setMsg = (msg, color) => setFeedback({ msg, color });

  const handleAdd = async () => {
    const weight = Number(catWeight);
    if (!catName.trim() || isNaN(weight) || weight < 0 || weight > 100) {
      setMsg('Enter a category name and a weight between 0 and 100.', 'red'); return;
    }
    const { error } = await supabase.from('competency_category')
      .insert([{ category_name: catName.trim(), category_weight: weight, organization_id: organizationId }]);
    if (error) { setMsg(error.message, 'red'); return; }
    await fetchCategories();
    setCatName(''); setCatWeight('');
    setMsg('Category added.', 'green');
  };

  const handleSaveEdit = async () => {
    const weight = Number(editItem.category_weight);
    if (!editItem.category_name.trim() || isNaN(weight) || weight < 0 || weight > 100) {
      setMsg('Enter a valid name and weight.', 'red'); return;
    }
    const { error } = await supabase.from('competency_category')
      .update({ category_name: editItem.category_name, category_weight: weight })
      .eq('category_id', editItem.category_id);
    if (error) { setMsg(error.message, 'red'); return; }
    await fetchCategories(); setEditItem(null); setMsg('Updated.', 'green');
  };

  const handleDeleteConfirm = async () => {
    const cat = deleteConfirm;
    setDeleteConfirm(null);

    // Step 1 — groups for this category (also fetch department_id for descriptions cascade)
    const { data: groups, error: gErr } = await supabase
      .from('competency_groups').select('group_id, department_id').eq('category_id', cat.category_id);
    if (gErr) { setMsg(gErr.message, 'red'); return; }

    if (groups?.length > 0) {
      const groupIds = groups.map(g => g.group_id);
      const deptIds  = [...new Set(groups.map(g => g.department_id).filter(Boolean))];

      // Step 2 — competencies in those groups
      const { data: comps, error: cErr } = await supabase
        .from('competencies').select('competency_id').in('group_id', groupIds);
      if (cErr) { setMsg(cErr.message, 'red'); return; }

      if (comps?.length > 0) {
        const compIds = comps.map(c => c.competency_id);

        // Step 3 — get relevance_level rows so we can unblock competencyrelevanceperformance first
        const { data: relLevels } = await supabase
          .from('competency_relevance_levels').select('relevance_id').in('competency_id', compIds);

        // Step 4 — delete competencyrelevanceperformance BEFORE competency_relevance_levels
        //           it has FKs on BOTH relevance_id and competency_id, cover both
        if (relLevels?.length > 0) {
          const relIds = relLevels.map(r => r.relevance_id);
          const { error: crpRlErr } = await supabase.from('competencyrelevanceperformance').delete().in('relevance_id', relIds);
          if (crpRlErr) { setMsg(crpRlErr.message, 'red'); return; }
        }
        const { error: crpErr } = await supabase.from('competencyrelevanceperformance').delete().in('competency_id', compIds);
        if (crpErr) { setMsg(crpErr.message, 'red'); return; }

        // Step 5 — now safe to delete competency_relevance_levels
        const { error: crlErr } = await supabase.from('competency_relevance_levels').delete().in('competency_id', compIds);
        if (crlErr) { setMsg(crlErr.message, 'red'); return; }

        // Step 6 — delete competency_descriptions using department_id filter first
        //           (mirrors corporateStructure.jsx which is the proven working path via RLS),
        //           then a direct competency_id delete catches any null-department rows
        if (deptIds.length > 0) {
          const { error: cdDeptErr } = await supabase.from('competency_descriptions')
            .delete().in('department_id', deptIds).in('competency_id', compIds);
          if (cdDeptErr) { setMsg(cdDeptErr.message, 'red'); return; }
        }
        const { error: cdErr } = await supabase.from('competency_descriptions')
          .delete().in('competency_id', compIds);
        if (cdErr) { setMsg(cdErr.message, 'red'); return; }

        // Step 7 — remaining children of competencies
        const { error: ciErr } = await supabase.from('competency_items').delete().in('competency_id', compIds);
        if (ciErr) { setMsg(ciErr.message, 'red'); return; }

        const { error: rcErr } = await supabase.from('rolecompetencies').delete().in('competency_id', compIds);
        if (rcErr) { setMsg(rcErr.message, 'red'); return; }

        // Step 8 — delete competencies (all children cleared above)
        const { error: delCErr } = await supabase.from('competencies').delete().in('competency_id', compIds);
        if (delCErr) { setMsg(delCErr.message, 'red'); return; }
      }
    }

    // Step 9 — delete the category; DB CASCADE handles competency_groups automatically
    const { error } = await supabase.from('competency_category').delete().eq('category_id', cat.category_id);
    if (error) { setMsg(error.message, 'red'); return; }

    setCategories(prev => prev.filter(c => c.category_id !== cat.category_id));
    setMsg('Category deleted.', 'green');
  };

  return (
    <div className="cm__step">
      <h2 className="cm__step-title">Step 1: Competency Categories</h2>
      <p className="cm__step-desc">
        Define your organisation's competency categories and their global weightings.
        These will appear as tab headers in the overview.
      </p>

      {editItem && (
        <div className="cm__modal-overlay">
          <div className="cm__modal">
            <h3 className="cm__modal-title">Edit Category</h3>
            <div className="cm__modal-fields">
              <input type="text" value={editItem.category_name}
                onChange={e => setEditItem({ ...editItem, category_name: e.target.value })} placeholder="Category Name" />
              <input type="number" value={editItem.category_weight}
                onChange={e => setEditItem({ ...editItem, category_weight: e.target.value })}
                placeholder="Weight (%)" min="0" max="100" />
            </div>
            <div className="cm__modal-actions">
              <button className="cm__modal-cancel" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="cm__modal-save" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmModal
          message={<>Delete <strong>{deleteConfirm.category_name}</strong>?</>}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <div className="cm__form-row">
        <label>
          <span className="cm__form-label">Category Name</span>
          <input type="text" value={catName} onChange={e => setCatName(e.target.value)}
            placeholder="e.g. Core Competencies" />
        </label>
        <label>
          <span className="cm__form-label">Weight (%)</span>
          <input type="number" value={catWeight} onChange={e => setCatWeight(e.target.value)}
            placeholder="Weight (%)" min="0" max="100" />
        </label>
        <button type="button" className="cm__add-btn" onClick={handleAdd}>Add</button>
      </div>

      <table className="cm__config-table">
        <thead><tr><th>Category Name</th><th>Weight (%)</th><th>Actions</th></tr></thead>
        <tbody>
          {categories.length === 0 && (
            <tr><td colSpan="3" className="cm__tbl-empty">No categories yet. Add one above.</td></tr>
          )}
          {categories.map(cat => (
            <tr key={cat.category_id}>
              <td>{cat.category_name}</td>
              <td>{cat.category_weight}%</td>
              <td>
                <div className="cm__tbl-actions">
                  <button type="button" className="cm__tbl-btn" onClick={() => setEditItem({ ...cat })}>Edit</button>
                  <button type="button" className="cm__tbl-btn cm__tbl-btn--del" onClick={() => setDeleteConfirm(cat)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Feedback {...feedback} />

      <div className="cm__step-nav">
        <span />
        <button type="button" className="cm__nav-btn cm__nav-btn--next" onClick={onNext}
          disabled={categories.length === 0}>
          Next <i className="bi bi-arrow-right" />
        </button>
      </div>
    </div>
  );
};

// ── Configure Step 2: Groups ──────────────────────────────────────────────────

const Step2Groups = ({ organizationId, onBack, onNext }) => {
  const [departments, setDepartments]     = useState([]);
  const [groups, setGroups]               = useState([]);        // displayed table
  const [scope, setScope]                 = useState('corp');
  const [filterDeptId, setFilterDeptId]   = useState('');
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [groupName, setGroupName]         = useState('');
  const [groupDesc, setGroupDesc]         = useState('');
  const [groupWeight, setGroupWeight]     = useState('');
  const [editItem, setEditItem]           = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [undoState, setUndoState]         = useState(null);
  const [feedback, setFeedback]           = useState({ msg: '', color: '' });
  const undoTimeoutRef = useRef(null);
  const countdownRef   = useRef(null);

  useEffect(() => {
    if (!organizationId) return;
    supabase.from('department').select('department_id, depart_name')
      .eq('organization_id', organizationId).order('depart_name')
      .then(({ data }) => {
        const rows = data || [];
        setDepartments(rows);
        if (rows.length > 0) { setSelectedDeptId(rows[0].department_id); setFilterDeptId(rows[0].department_id); }
      });
  }, [organizationId]);

  const fetchGroups = useCallback(async () => {
    if (scope === 'corp') {
      const { data } = await supabase.from('corp_competence_groups').select('*')
        .eq('organization_id', organizationId).order('created_at');
      setGroups((data || []).map(g => ({ ...g, _source: 'corp' })));
    } else {
      if (!filterDeptId) { setGroups([]); return; }
      const { data } = await supabase.from('func_competence_groups').select('*')
        .eq('department_id', filterDeptId).eq('organization_id', organizationId).order('created_at');
      setGroups((data || []).map(g => ({ ...g, _source: 'func' })));
    }
  }, [scope, filterDeptId, organizationId]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);
  useEffect(() => () => { clearInterval(countdownRef.current); }, []);

  const setMsg = (msg, color) => setFeedback({ msg, color });

  const handleAdd = async () => {
    const weight = Number(groupWeight);
    if (!groupName.trim() || isNaN(weight) || weight < 0 || weight > 100) {
      setMsg('Enter a group name and a weight between 0 and 100.', 'red'); return;
    }
    if (scope === 'func' && !selectedDeptId) {
      setMsg('Select a department for a Functional Talent Strategy group.', 'red'); return;
    }

    if (scope === 'corp') {
      const { error } = await supabase.from('corp_competence_groups').insert([{
        group_name: groupName.trim(),
        description: groupDesc.trim() || null,
        weighting: weight,
        organization_id: organizationId,
      }]);
      if (error) { setMsg(error.message, 'red'); return; }
    } else {
      const { error } = await supabase.from('func_competence_groups').insert([{
        group_name: groupName.trim(),
        description: groupDesc.trim() || null,
        group_weight: weight,
        department_id: selectedDeptId,
        organization_id: organizationId,
      }]);
      if (error) { setMsg(error.message, 'red'); return; }
    }

    await fetchGroups();
    setGroupName(''); setGroupDesc(''); setGroupWeight('');
    setMsg('Group added.', 'green');
  };

  const handleSaveEdit = async () => {
    const weight = Number(editItem._source === 'corp' ? editItem.weighting : editItem.group_weight);
    if (!editItem.group_name.trim() || isNaN(weight) || weight < 0 || weight > 100) {
      setMsg('Enter a valid name and weight.', 'red'); return;
    }
    if (editItem._source === 'corp') {
      const { error } = await supabase.from('corp_competence_groups')
        .update({ group_name: editItem.group_name, description: editItem.description || null, weighting: weight })
        .eq('group_id', editItem.group_id);
      if (error) { setMsg(error.message, 'red'); return; }
    } else {
      const { error } = await supabase.from('func_competence_groups')
        .update({ group_name: editItem.group_name, description: editItem.description || null, group_weight: weight })
        .eq('group_id', editItem.group_id);
      if (error) { setMsg(error.message, 'red'); return; }
    }
    await fetchGroups(); setEditItem(null); setMsg('Updated.', 'green');
  };

  const startUndo = (item) => {
    clearTimeout(undoTimeoutRef.current); clearInterval(countdownRef.current);
    setGroups(prev => prev.filter(g => !(g.group_id === item.group_id && g._source === item._source)));
    let remaining = 3;
    setUndoState({ item, countdown: remaining });
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setUndoState(prev => prev ? { ...prev, countdown: remaining } : null);
    }, 1000);
    undoTimeoutRef.current = setTimeout(async () => {
      clearInterval(countdownRef.current); setUndoState(null);
      const table = item._source === 'corp' ? 'corp_competence_groups' : 'func_competence_groups';
      await supabase.from(table).delete().eq('group_id', item.group_id);
    }, 3000);
  };

  const handleUndo = () => {
    clearTimeout(undoTimeoutRef.current); clearInterval(countdownRef.current);
    setUndoState(null); fetchGroups();
  };

  return (
    <div className="cm__step">
      <h2 className="cm__step-title">Step 2: Competency Groups</h2>
      <p className="cm__step-desc">
        Add competency groups and assign each a scope: <strong>Corporate Talent Strategy</strong> groups apply to the whole
        organisation; <strong>Functional Talent Strategy</strong> groups apply to a specific department.
      </p>

      {undoState && <UndoToast message="Group deleted." countdown={undoState.countdown} onUndo={handleUndo} />}

      {editItem && (
        <div className="cm__modal-overlay">
          <div className="cm__modal">
            <h3 className="cm__modal-title">Edit Competency Group</h3>
            <div className="cm__modal-fields">
              <input type="text" value={editItem.group_name}
                onChange={e => setEditItem({ ...editItem, group_name: e.target.value })} placeholder="Group Name" />
              <input type="text" value={editItem.description || ''}
                onChange={e => setEditItem({ ...editItem, description: e.target.value })} placeholder="Description" />
              <input type="number"
                value={editItem._source === 'corp' ? editItem.weighting : editItem.group_weight}
                onChange={e => setEditItem({
                  ...editItem,
                  ...(editItem._source === 'corp' ? { weighting: e.target.value } : { group_weight: e.target.value }),
                })}
                placeholder="Weight (%)" min="0" max="100" step="0.1" />
            </div>
            <div className="cm__modal-actions">
              <button className="cm__modal-cancel" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="cm__modal-save" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmModal
          message={<>Delete <strong>{deleteConfirm.group_name}</strong>? Competencies in this group will also be removed.</>}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={() => { setDeleteConfirm(null); setFeedback({ msg: '', color: '' }); startUndo(deleteConfirm); }}
        />
      )}

      {/* Add form — scope first, then other fields */}
      <div className="cm__group-form-card">
        <div className="cm__form-row cm__form-row--scope">
          <label style={{ maxWidth: 320 }}>
            <span className="cm__form-label">Scope</span>
            <select value={scope} onChange={e => { setScope(e.target.value); setGroupName(''); setGroupDesc(''); setGroupWeight(''); }}>
              <option value="corp">Corporate Talent Strategy</option>
              <option value="func">Functional Talent Strategy</option>
            </select>
          </label>
          {scope === 'func' && (
            <label style={{ maxWidth: 280 }}>
              <span className="cm__form-label">Department</span>
              <select value={selectedDeptId} onChange={e => setSelectedDeptId(e.target.value)}>
                <option value="">Select Department</option>
                {departments.map(d => (
                  <option key={d.department_id} value={d.department_id}>{d.depart_name}</option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="cm__form-row">
          <label>
            <span className="cm__form-label">Group Name</span>
            <input type="text" value={groupName} onChange={e => setGroupName(e.target.value)}
              placeholder="Competency Group Name" />
          </label>
          <label>
            <span className="cm__form-label">Description</span>
            <input type="text" value={groupDesc} onChange={e => setGroupDesc(e.target.value)}
              placeholder="Brief description" />
          </label>
          <label style={{ maxWidth: 140 }}>
            <span className="cm__form-label">Weight (%)</span>
            <input type="number" value={groupWeight} onChange={e => setGroupWeight(e.target.value)}
              placeholder="Weight (%)" min="0" max="100" step="0.1" />
          </label>
          <button type="button" className="cm__add-btn" onClick={handleAdd}>Add</button>
        </div>
      </div>

      {/* Filter bar + table */}
      <div className="cm__filter-bar">
        <span className="cm__form-label">Viewing:</span>
        <select className="cm__dept-select" value={scope}
          onChange={e => { setScope(e.target.value); }}>
          <option value="corp">Corporate Talent Strategy</option>
          <option value="func">Functional Talent Strategy</option>
        </select>
        {scope === 'func' && departments.length > 0 && (
          <select className="cm__dept-select" value={filterDeptId}
            onChange={e => setFilterDeptId(e.target.value)}>
            <option value="">All Departments</option>
            {departments.map(d => (
              <option key={d.department_id} value={d.department_id}>{d.depart_name}</option>
            ))}
          </select>
        )}
      </div>

      <table className="cm__config-table">
        <thead>
          <tr><th>Group Name</th><th>Description</th><th>Weight</th><th>Actions</th></tr>
        </thead>
        <tbody>
          {groups.length === 0 && (
            <tr><td colSpan="4" className="cm__tbl-empty">No groups yet for this scope.</td></tr>
          )}
          {groups.map(g => (
            <tr key={`${g._source}-${g.group_id}`}>
              <td>{g.group_name}</td>
              <td>{g.description || '—'}</td>
              <td>{g._source === 'corp' ? g.weighting : g.group_weight}%</td>
              <td>
                <div className="cm__tbl-actions">
                  <button type="button" className="cm__tbl-btn" onClick={() => setEditItem({ ...g })}>Edit</button>
                  <button type="button" className="cm__tbl-btn cm__tbl-btn--del" onClick={() => setDeleteConfirm(g)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Feedback {...feedback} />

      <div className="cm__step-nav">
        <button type="button" className="cm__nav-btn" onClick={onBack}><i className="bi bi-arrow-left" /> Previous</button>
        <button type="button" className="cm__nav-btn cm__nav-btn--next" onClick={onNext}>Next <i className="bi bi-arrow-right" /></button>
      </div>
    </div>
  );
};

// ── Configure Step 3: Competencies ────────────────────────────────────────────

const Step3Competencies = ({ organizationId, onBack, onNext }) => {
  const [allGroups, setAllGroups]         = useState([]); // both corp + func, tagged with _source
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [competencies, setCompetencies]   = useState([]);
  const [compName, setCompName]           = useState('');
  const [compCode, setCompCode]           = useState('');
  const [compProg, setCompProg]           = useState('');
  const [editItem, setEditItem]           = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [undoState, setUndoState]         = useState(null);
  const [feedback, setFeedback]           = useState({ msg: '', color: '' });
  const undoTimeoutRef = useRef(null);
  const countdownRef   = useRef(null);

  // Load all groups from both tables on mount
  useEffect(() => {
    if (!organizationId) return;
    Promise.all([
      supabase.from('corp_competence_groups').select('group_id, group_name').eq('organization_id', organizationId).order('created_at'),
      supabase.from('func_competence_groups').select('group_id, group_name, department_id, depart:department(depart_name)').eq('organization_id', organizationId).order('created_at'),
    ]).then(([{ data: corp }, { data: func }]) => {
      const rows = [
        ...(corp || []).map(g => ({ ...g, _source: 'corp', label: `${g.group_name} (Corporate Talent Strategy)` })),
        ...(func || []).map(g => ({ ...g, _source: 'func', label: `${g.group_name} — ${g.depart?.depart_name || 'Dept'} (Functional Talent Strategy)` })),
      ];
      setAllGroups(rows);
      if (rows.length > 0) setSelectedGroup(rows[0]);
    });
  }, [organizationId]);

  const fetchComps = useCallback(async (group) => {
    if (!group) { setCompetencies([]); return; }
    if (group._source === 'corp') {
      const { data } = await supabase.from('corp_competence_items').select('*')
        .eq('group_id', group.group_id).order('created_at');
      setCompetencies((data || []).map(c => ({ ...c, _source: 'corp', display_name: c.competency_name })));
    } else {
      const { data } = await supabase.from('func_competence_items').select('*')
        .eq('group_id', group.group_id).order('created_at');
      setCompetencies((data || []).map(c => ({ ...c, _source: 'func', display_name: c.item_name })));
    }
  }, []);

  useEffect(() => { fetchComps(selectedGroup); }, [selectedGroup, fetchComps]);
  useEffect(() => () => { clearInterval(countdownRef.current); }, []);

  const setMsg = (msg, color) => setFeedback({ msg, color });

  const handleAdd = async () => {
    if (!selectedGroup) { setMsg('Select a competency group.', 'red'); return; }
    if (!compName.trim()) { setMsg('Enter a competency name.', 'red'); return; }

    if (selectedGroup._source === 'corp') {
      const { error } = await supabase.from('corp_competence_items').insert([{
        competency_name: compName.trim(),
        competency_code: compCode.trim() || null,
        progression: compProg.trim() || null,
        group_id: selectedGroup.group_id,
        organization_id: organizationId,
      }]);
      if (error) { setMsg(error.message, 'red'); return; }
    } else {
      const { error } = await supabase.from('func_competence_items').insert([{
        item_name: compName.trim(),
        item_code: compCode.trim() || null,
        progression: compProg.trim() || null,
        group_id: selectedGroup.group_id,
        organization_id: organizationId,
      }]);
      if (error) { setMsg(error.message, 'red'); return; }
    }

    await fetchComps(selectedGroup);
    setCompName(''); setCompCode(''); setCompProg('');
    setMsg('Competency added.', 'green');
  };

  const handleSaveEdit = async () => {
    const name = editItem._source === 'corp' ? editItem.competency_name : editItem.item_name;
    if (!name.trim()) { setMsg('Name is required.', 'red'); return; }

    if (editItem._source === 'corp') {
      const { error } = await supabase.from('corp_competence_items')
        .update({ competency_name: editItem.competency_name, competency_code: editItem.competency_code || null, progression: editItem.progression || null })
        .eq('competency_id', editItem.competency_id);
      if (error) { setMsg(error.message, 'red'); return; }
    } else {
      const { error } = await supabase.from('func_competence_items')
        .update({ item_name: editItem.item_name, item_code: editItem.item_code || null, progression: editItem.progression || null })
        .eq('item_id', editItem.item_id);
      if (error) { setMsg(error.message, 'red'); return; }
    }
    await fetchComps(selectedGroup); setEditItem(null); setMsg('Updated.', 'green');
  };

  const getItemId = (item) => item._source === 'corp' ? item.competency_id : item.item_id;

  const startUndo = (item) => {
    clearTimeout(undoTimeoutRef.current); clearInterval(countdownRef.current);
    const id = getItemId(item);
    setCompetencies(prev => prev.filter(c => getItemId(c) !== id));
    let remaining = 3;
    setUndoState({ item, countdown: remaining });
    countdownRef.current = setInterval(() => {
      remaining -= 1;
      setUndoState(prev => prev ? { ...prev, countdown: remaining } : null);
    }, 1000);
    undoTimeoutRef.current = setTimeout(async () => {
      clearInterval(countdownRef.current); setUndoState(null);
      if (item._source === 'corp') {
        await supabase.from('corp_competence_items').delete().eq('competency_id', item.competency_id);
      } else {
        await supabase.from('func_competence_items').delete().eq('item_id', item.item_id);
      }
    }, 3000);
  };

  const handleUndo = () => {
    clearTimeout(undoTimeoutRef.current); clearInterval(countdownRef.current);
    setUndoState(null); fetchComps(selectedGroup);
  };

  return (
    <div className="cm__step">
      <h2 className="cm__step-title">Step 3: Competencies</h2>
      <p className="cm__step-desc">
        Add individual competencies to each group. Select a group to view and manage its competencies.
      </p>

      {undoState && <UndoToast message="Competency deleted." countdown={undoState.countdown} onUndo={handleUndo} />}

      {editItem && (
        <div className="cm__modal-overlay">
          <div className="cm__modal">
            <h3 className="cm__modal-title">Edit Competency</h3>
            <div className="cm__modal-fields">
              {editItem._source === 'corp' ? (
                <>
                  <input type="text" value={editItem.competency_code || ''}
                    onChange={e => setEditItem({ ...editItem, competency_code: e.target.value })} placeholder="Code (e.g. PPC – A1)" />
                  <input type="text" value={editItem.competency_name}
                    onChange={e => setEditItem({ ...editItem, competency_name: e.target.value })} placeholder="Competency Name" />
                </>
              ) : (
                <>
                  <input type="text" value={editItem.item_code || ''}
                    onChange={e => setEditItem({ ...editItem, item_code: e.target.value })} placeholder="Code (e.g. PPC – A1)" />
                  <input type="text" value={editItem.item_name}
                    onChange={e => setEditItem({ ...editItem, item_name: e.target.value })} placeholder="Competency Name" />
                </>
              )}
              <textarea value={editItem.progression || ''}
                onChange={e => setEditItem({ ...editItem, progression: e.target.value })}
                placeholder="Description / Progression" rows={3} />
            </div>
            <div className="cm__modal-actions">
              <button className="cm__modal-cancel" onClick={() => setEditItem(null)}>Cancel</button>
              <button className="cm__modal-save" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <ConfirmModal
          message={<>Delete <strong>{deleteConfirm.display_name}</strong>?</>}
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={() => { setDeleteConfirm(null); setFeedback({ msg: '', color: '' }); startUndo(deleteConfirm); }}
        />
      )}

      <div className="cm__form-row">
        <label>
          <span className="cm__form-label">Competency Group</span>
          <select value={selectedGroup ? `${selectedGroup._source}_${selectedGroup.group_id}` : ''}
            onChange={e => {
              const [src, id] = e.target.value.split('_');
              const g = allGroups.find(x => x._source === src && String(x.group_id) === id);
              setSelectedGroup(g || null);
            }}>
            <option value="">Select Group</option>
            {allGroups.map(g => (
              <option key={`${g._source}_${g.group_id}`} value={`${g._source}_${g.group_id}`}>{g.label}</option>
            ))}
          </select>
        </label>
        <label>
          <span className="cm__form-label">Code</span>
          <input type="text" value={compCode} onChange={e => setCompCode(e.target.value)} placeholder="e.g. PPC – A1" />
        </label>
        <label>
          <span className="cm__form-label">Competency Name</span>
          <input type="text" value={compName} onChange={e => setCompName(e.target.value)} placeholder="Enter Competency Name" />
        </label>
        <button type="button" className="cm__add-btn" onClick={handleAdd}>Add</button>
      </div>

      <div className="cm__progression-wrap">
        <label className="cm__form-label">Description / Progression</label>
        <textarea value={compProg} onChange={e => setCompProg(e.target.value)}
          placeholder="Describe the expected progression for this competency…" rows={3} />
      </div>

      {selectedGroup && (
        <div className="cm__group-context-bar">
          <span className={`cm__scope-label cm__scope-label--${selectedGroup._source === 'corp' ? 'corp' : 'func'}`}>
            <i className={`bi ${selectedGroup._source === 'corp' ? 'bi-building' : 'bi-diagram-3'}`} />
            {selectedGroup._source === 'corp' ? 'Corporate Talent Strategy' : 'Functional Talent Strategy'}
          </span>
          <span className="cm__group-context-name">{selectedGroup.group_name}</span>
        </div>
      )}

      <table className="cm__config-table cm__config-table--fixed">
        <colgroup><col style={{ width: '16%' }} /><col style={{ width: '30%' }} /><col style={{ width: '38%' }} /><col style={{ width: '16%' }} /></colgroup>
        <thead><tr><th>Code</th><th>Competency Name</th><th>Progression</th><th>Actions</th></tr></thead>
        <tbody>
          {competencies.length === 0 && (
            <tr><td colSpan="4" className="cm__tbl-empty">No competencies in this group yet.</td></tr>
          )}
          {competencies.map(c => (
            <tr key={`${c._source}_${getItemId(c)}`}>
              <td>{(c._source === 'corp' ? c.competency_code : c.item_code) || '—'}</td>
              <td>{c.display_name}</td>
              <td className="cm__progression-cell">{c.progression || '—'}</td>
              <td>
                <div className="cm__tbl-actions">
                  <button type="button" className="cm__tbl-btn" onClick={() => setEditItem({ ...c })}>Edit</button>
                  <button type="button" className="cm__tbl-btn cm__tbl-btn--del" onClick={() => setDeleteConfirm(c)}>Delete</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Feedback {...feedback} />

      <div className="cm__step-nav">
        <button type="button" className="cm__nav-btn" onClick={onBack}><i className="bi bi-arrow-left" /> Previous</button>
        <button type="button" className="cm__nav-btn cm__nav-btn--next" onClick={onNext}>Next <i className="bi bi-arrow-right" /></button>
      </div>
    </div>
  );
};

// ── Configure Step 4: Descriptions ───────────────────────────────────────────

const Step4Descriptions = ({ organizationId, onBack, onClose }) => {
  const [allGroups, setAllGroups]           = useState([]);
  const [competencies, setCompetencies]     = useState([]);
  const [descriptions, setDescriptions]     = useState([]);
  const [selectedGroup, setSelectedGroup]   = useState(null);
  const [selectedCompId, setSelectedCompId] = useState('');
  const [descText, setDescText]             = useState('');
  const [descLevel, setDescLevel]           = useState('1');
  const [feedback, setFeedback]             = useState({ msg: '', color: '' });

  useEffect(() => {
    if (!organizationId) return;
    Promise.all([
      supabase.from('corp_competence_groups').select('group_id, group_name').eq('organization_id', organizationId).order('created_at'),
      supabase.from('func_competence_groups').select('group_id, group_name').eq('organization_id', organizationId).order('created_at'),
    ]).then(([{ data: corp }, { data: func }]) => {
      const rows = [
        ...(corp || []).map(g => ({ ...g, _source: 'corp', label: `${g.group_name} (Corporate)` })),
        ...(func || []).map(g => ({ ...g, _source: 'func', label: `${g.group_name} (Functional Talent Strategy)` })),
      ];
      setAllGroups(rows);
      if (rows.length > 0) setSelectedGroup(rows[0]);
    });
  }, [organizationId]);

  useEffect(() => {
    if (!selectedGroup) { setCompetencies([]); setSelectedCompId(''); return; }
    const fetchC = async () => {
      if (selectedGroup._source === 'corp') {
        const { data } = await supabase.from('corp_competence_items').select('competency_id, competency_name')
          .eq('group_id', selectedGroup.group_id).order('created_at');
        const rows = (data || []).map(c => ({ id: c.competency_id, name: c.competency_name }));
        setCompetencies(rows);
        setSelectedCompId(rows.length > 0 ? String(rows[0].id) : '');
      } else {
        const { data } = await supabase.from('func_competence_items').select('item_id, item_name')
          .eq('group_id', selectedGroup.group_id).order('created_at');
        const rows = (data || []).map(c => ({ id: c.item_id, name: c.item_name }));
        setCompetencies(rows);
        setSelectedCompId(rows.length > 0 ? String(rows[0].id) : '');
      }
    };
    fetchC();
  }, [selectedGroup]);

  const fetchDescriptions = useCallback(async (compId) => {
    if (!compId) { setDescriptions([]); return; }
    const { data, error } = await supabase.from('competency_descriptions')
      .select('description_id, description_level, description_name')
      .eq('competency_id', Number(compId))
      .eq('organization_id', organizationId)
      .order('description_level', { ascending: true });
    if (error) { setMsg(error.message, 'red'); return; }
    setDescriptions(data || []);
  }, [organizationId]);

  useEffect(() => { fetchDescriptions(selectedCompId); }, [selectedCompId, fetchDescriptions]);

  const setMsg = (msg, color) => {
    setFeedback({ msg, color });
    setTimeout(() => setFeedback({ msg: '', color: '' }), 3000);
  };

  const handleAdd = async () => {
    const text = descText.trim();
    if (!text || !selectedCompId) { setMsg('Select a competency and enter a description.', 'red'); return; }
    const { error } = await supabase.from('competency_descriptions').insert([{
      competency_id: Number(selectedCompId),
      description_name: text,
      description_level: Number(descLevel),
      organization_id: organizationId,
    }]);
    if (error) { setMsg(error.message, 'red'); return; }
    await fetchDescriptions(selectedCompId);
    setDescText('');
    setMsg('Description added.', 'green');
  };

  const handleDelete = async (item) => {
    const { error } = await supabase.from('competency_descriptions').delete().eq('description_id', item.description_id);
    if (error) { setMsg('Error deleting.', 'red'); return; }
    setDescriptions(prev => prev.filter(d => d.description_id !== item.description_id));
  };

  return (
    <div className="cm__step">
      <h2 className="cm__step-title">Step 4: Competency Descriptions</h2>
      <p className="cm__step-desc">
        Add behavioural descriptors for each competency across the four career levels.
      </p>

      <div className="cm__desc-selectors">
        <div className="cm__desc-selector-group">
          <label className="cm__form-label">Select Competency Group</label>
          <select value={selectedGroup ? `${selectedGroup._source}_${selectedGroup.group_id}` : ''}
            onChange={e => {
              if (!e.target.value) { setSelectedGroup(null); return; }
              const [src, id] = e.target.value.split('_');
              const g = allGroups.find(x => x._source === src && String(x.group_id) === id);
              setSelectedGroup(g || null);
              setSelectedCompId(''); setDescriptions([]);
            }}>
            <option value="">Select a group</option>
            {allGroups.map(g => (
              <option key={`${g._source}_${g.group_id}`} value={`${g._source}_${g.group_id}`}>{g.label}</option>
            ))}
          </select>
        </div>
        <div className="cm__desc-selector-group">
          <label className="cm__form-label">Select Competency</label>
          <select value={selectedCompId} onChange={e => setSelectedCompId(e.target.value)}>
            <option value="">Select a competency</option>
            {competencies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div className="cm__desc-input-section">
        <div className="cm__desc-input-group">
          <label className="cm__form-label">Description</label>
          <textarea value={descText} onChange={e => setDescText(e.target.value)}
            placeholder="Enter a behavioural descriptor for this competency…" rows={4} />
        </div>
        <div className="cm__desc-level-row">
          <div>
            <label className="cm__form-label">Level</label>
            <select value={descLevel} onChange={e => setDescLevel(e.target.value)}>
              {[1, 2, 3, 4].map(l => <option key={l} value={l}>{l} — {getLevelName(l)}</option>)}
            </select>
          </div>
          <button type="button" className="cm__add-btn" onClick={handleAdd}>Add Description</button>
        </div>
      </div>

      <Feedback {...feedback} />

      <h3 className="cm__step-title" style={{ marginTop: '24px', fontSize: '15px' }}>Competency Level Descriptions</h3>
      <div className="cm__level-table-wrap">
        <table className="cm__level-table">
          <thead>
            <tr>{[1, 2, 3, 4].map(l => <th key={l}>{getLevelName(l)}</th>)}</tr>
          </thead>
          <tbody>
            <tr>
              {[1, 2, 3, 4].map(l => {
                const lvlDescs = descriptions.filter(d => d.description_level === l);
                return (
                  <td key={l} className="cm__level-cell">
                    {lvlDescs.length === 0
                      ? <span className="cm__level-empty">No description added</span>
                      : lvlDescs.map((d, i) => (
                          <div key={i} className="cm__desc-item">
                            <span className="cm__desc-text">{d.description_name}</span>
                            <button type="button" className="cm__desc-del-btn" title="Delete"
                              onClick={() => handleDelete(d)}>
                              <i className="bi bi-trash3" />
                            </button>
                          </div>
                        ))
                    }
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="cm__step-nav">
        <button type="button" className="cm__nav-btn" onClick={onBack}><i className="bi bi-arrow-left" /> Previous</button>
        <button type="button" className="cm__nav-btn cm__nav-btn--next" onClick={onClose}>
          <i className="bi bi-check-lg" /> Save &amp; Close
        </button>
      </div>
    </div>
  );
};

// ── Configure wizard wrapper ──────────────────────────────────────────────────

const ConfigureWizard = ({ organizationId, onClose }) => {
  const [step, setStep] = useState(1);
  return (
    <div className="cm__page">
      <div className="cm__topbar">
        <div className="cm__topbar-left">
          <h1 className="cm__title"><i className="bi bi-bullseye" /> Competency Model — Configuration</h1>
          <p className="cm__subtitle">Build and manage your organisation's full competency model.</p>
        </div>
        <button className="cm__btn cm__btn--configure" onClick={onClose}>
          <i className="bi bi-x-lg" /> Close
        </button>
      </div>
      <hr className="cm__hr" />
      <Breadcrumb step={step} onNav={setStep} />
      <hr className="cm__hr" style={{ marginTop: '8px' }} />

      {step === 1 && <Step1Categories organizationId={organizationId} onNext={() => setStep(2)} />}
      {step === 2 && <Step2Groups organizationId={organizationId} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
      {step === 3 && <Step3Competencies organizationId={organizationId} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
      {step === 4 && <Step4Descriptions organizationId={organizationId} onBack={() => setStep(3)} onClose={onClose} />}
    </div>
  );
};

// ── Root ──────────────────────────────────────────────────────────────────────

const CompetencyModel = () => {
  const { organizationId } = useAuth();
  const [view, setView]             = useState('loading');
  const [hasCategories, setHasCategories] = useState(false);

  const checkCategories = useCallback(() => {
    if (!organizationId) return;
    supabase.from('competency_category').select('category_id', { count: 'exact', head: true })
      .eq('organization_id', organizationId)
      .then(({ count }) => {
        const exists = (count || 0) > 0;
        setHasCategories(exists);
        setView(exists ? 'overview' : 'welcome');
      });
  }, [organizationId]);

  useEffect(() => { checkCategories(); }, [checkCategories]);

  if (view === 'loading') return <div className="cm__loading"><span className="cm__spinner" /> Loading…</div>;

  if (view === 'configure') {
    return (
      <ConfigureWizard
        organizationId={organizationId}
        onClose={() => { checkCategories(); }}
      />
    );
  }

  if (view === 'welcome' || !hasCategories) {
    return (
      <div className="cm__page">
        <WelcomeScreen onGetStarted={() => setView('configure')} />
      </div>
    );
  }

  return (
    <div className="cm__page">
      <Overview organizationId={organizationId} onConfigure={() => setView('configure')} />
    </div>
  );
};

export default CompetencyModel;
